import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle, FormControlLabel, MenuItem,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton, GlassIconButton } from 'dashboard/components/common';
import OpeningHoursField from './OpeningHoursField';

/**
 * Setting up something people can book.
 *
 * The words are the owner's, not the schema's: "how many people fit at once"
 * rather than capacity, "how long does one booking last" rather than duration.
 * Every field here has a number in it already, because the defaults are what
 * most businesses want and an empty form is a quiz.
 */

const ZONES = [
  'Europe/Prague', 'Europe/Berlin', 'Europe/London', 'Europe/Paris',
  'Europe/Madrid', 'Europe/Warsaw', 'America/New_York', 'America/Los_Angeles',
];

const BLANK = {
  name: '',
  description: '',
  duration_minutes: 60,
  interval_minutes: 60,
  capacity: 1,
  max_party_size: 0,
  lead_time_minutes: 60,
  booking_window_days: 60,
  cancellation_hours: 24,
  auto_confirm: true,
  timezone: 'Europe/Prague',
  opening_hours: [
    { weekday: 2, opens: 17 * 60, closes: 21 * 60 },
    { weekday: 3, opens: 17 * 60, closes: 21 * 60 },
    { weekday: 4, opens: 17 * 60, closes: 21 * 60 },
    { weekday: 5, opens: 17 * 60, closes: 22 * 60 },
    { weekday: 6, opens: 17 * 60, closes: 22 * 60 },
  ],
};

const num = (v, fallback) => (v === '' || v === null ? fallback : Number(v));

export default function ResourceDialog({ open, onClose, brandId, resource, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(resource ? { ...BLANK, ...resource } : BLANK);
  }, [open, resource]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = () => {
    if (!form.name.trim()) { toast.error('Give it a name first'); return; }
    setSaving(true);
    const call = resource
      ? API.updateBookableResource({ brand_id: brandId, _id: resource._id, patch: form })
      : API.createBookableResource({ brand_id: brandId, patch: form });
    call
      .then(() => { toast.success(resource ? 'Saved' : 'Added'); onSaved(); onClose(); })
      .catch((err) => toast.error(err?.message || 'Could not save it'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {resource ? 'Edit' : 'Something people can book'}
        <GlassIconButton
          label='Close' onClick={onClose} size='small'
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing='16px'>
          <TextField
            size='small' label='What is it called' value={form.name} autoFocus
            onChange={(e) => set({ name: e.target.value })}
            placeholder='Dining room · Chair 1 · Treatment room'
            helperText='What a customer will see when they choose.'
          />
          <TextField
            size='small' label='A line about it' value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder='Window tables, quieter end of the room'
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing='12px'>
            <TextField
              size='small' type='number' label='How long one booking lasts'
              value={form.duration_minutes}
              onChange={(e) => set({ duration_minutes: num(e.target.value, 60) })}
              helperText='minutes' sx={{ flex: 1 }}
            />
            <TextField
              size='small' type='number' label='A new slot starts every'
              value={form.interval_minutes}
              onChange={(e) => set({ interval_minutes: num(e.target.value, 60) })}
              helperText='minutes — set it shorter than the booking to overlap'
              sx={{ flex: 1 }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing='12px'>
            <TextField
              size='small' type='number' label='How many people fit at once'
              value={form.capacity}
              onChange={(e) => set({ capacity: num(e.target.value, 1) })}
              helperText='1 for a chair, 26 for a dining room' sx={{ flex: 1 }}
            />
            <TextField
              size='small' type='number' label='Biggest single booking'
              value={form.max_party_size}
              onChange={(e) => set({ max_party_size: num(e.target.value, 0) })}
              helperText='0 means as many as fit' sx={{ flex: 1 }}
            />
          </Stack>

          <Typography variant='caption' fontWeight='700' textTransform='uppercase' color='text.secondary'>
            When it can be booked
          </Typography>
          <OpeningHoursField
            value={form.opening_hours}
            onChange={(opening_hours) => set({ opening_hours })}
          />

          <TextField
            size='small' select label='Times are local to'
            value={form.timezone}
            onChange={(e) => set({ timezone: e.target.value })}
            helperText='Where the business is — not where the server is.'
          >
            {ZONES.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing='12px'>
            <TextField
              size='small' type='number' label='Shortest notice'
              value={form.lead_time_minutes}
              onChange={(e) => set({ lead_time_minutes: num(e.target.value, 0) })}
              helperText='minutes before the slot' sx={{ flex: 1 }}
            />
            <TextField
              size='small' type='number' label='How far ahead'
              value={form.booking_window_days}
              onChange={(e) => set({ booking_window_days: num(e.target.value, 60) })}
              helperText='days' sx={{ flex: 1 }}
            />
            <TextField
              size='small' type='number' label='Cancel up to'
              value={form.cancellation_hours}
              onChange={(e) => set({ cancellation_hours: num(e.target.value, 24) })}
              helperText='hours before' sx={{ flex: 1 }}
            />
          </Stack>

          <FormControlLabel
            control={(
              <Switch
                checked={form.auto_confirm !== false}
                onChange={(e) => set({ auto_confirm: e.target.checked })}
              />
            )}
            label='Confirm bookings automatically'
          />
          <Typography variant='caption' color='text.secondary' mt='-8px'>
            {form.auto_confirm !== false
              ? 'The customer is told straight away that they have it.'
              : 'Bookings wait for you to confirm them. A request nobody reads is worse for the customer than either answer, so check the diary daily.'}
          </Typography>

          <Stack direction='row' justifyContent='flex-end' spacing='8px'>
            <GlassButton onClick={onClose}>Cancel</GlassButton>
            <GlassButton
              variant='contained' onClick={save}
              disabled={saving}
              disabledReason={saving ? 'Saving…' : ''}
            >
              {resource ? 'Save' : 'Add'}
            </GlassButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
